import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ArrowLeft,
  Pencil,
  Trash2,
  MoreHorizontal,
  Mail,
  Phone,
  Building2,
  Star,
  Tag,
  Clock,
  CheckCircle,
  XCircle,
  User,
  Activity,
  ClipboardList,
  MapPin,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiV1 } from '@/api/client';

const stageConfig = {
  new:         { label: 'New',         color: 'bg-blue-500' },
  contacted:   { label: 'Contacted',   color: 'bg-cyan-500' },
  qualified:   { label: 'Qualified',   color: 'bg-amber-500' },
  proposal:    { label: 'Proposal',    color: 'bg-purple-500' },
  negotiation: { label: 'Negotiation', color: 'bg-orange-500' },
  won:         { label: 'Won',         color: 'bg-emerald-500' },
  lost:        { label: 'Lost',        color: 'bg-red-500' },
};

const priorityConfig = {
  low:    { label: 'Low',    color: 'bg-slate-400' },
  medium: { label: 'Medium', color: 'bg-blue-400' },
  high:   { label: 'High',   color: 'bg-orange-400' },
  urgent: { label: 'Urgent', color: 'bg-red-500' },
};

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    priority: 'medium',
    tags_csv: '',
  });

  useEffect(() => {
    fetchLead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchLead = async () => {
    setLoading(true);
    try {
      const response = await apiV1.get(`/leads/${id}`);
      if (response.data?.success) {
        const data = response.data.data;
        setLead(data);
        setEditForm({
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || '',
          company: data.company || '',
          priority: data.priority || 'medium',
          tags_csv: (data.tags || []).join(', '),
        });
      }
    } catch (error) {
      console.error('Error fetching lead:', error);
      toast.error('Failed to load lead');
      navigate('/leads');
    } finally {
      setLoading(false);
    }
  };

  const handleStageChange = async (newStage) => {
    try {
      await apiV1.post(`/leads/${id}/stage/${newStage}`);
      toast.success(`Stage changed to ${stageConfig[newStage]?.label || newStage}`);
      fetchLead();
    } catch (error) {
      toast.error('Failed to change stage');
    }
  };

  const handleConvert = async () => {
    if (!window.confirm('Mark this lead as converted (Won)?')) return;
    try {
      await apiV1.post(`/leads/${id}/convert`, {});
      toast.success('Lead converted');
      fetchLead();
    } catch (error) {
      toast.error('Failed to convert lead');
    }
  };

  const handleMarkLost = async () => {
    const reason = window.prompt('Reason for marking lost? (optional)') ?? '';
    try {
      await apiV1.post(`/leads/${id}/lost`, null, { params: reason ? { reason } : {} });
      toast.success('Lead marked as lost');
      fetchLead();
    } catch (error) {
      toast.error('Failed to mark as lost');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Archive this lead?')) return;
    try {
      await apiV1.delete(`/leads/${id}`);
      toast.success('Lead archived');
      navigate('/leads');
    } catch (error) {
      toast.error('Failed to archive lead');
    }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        name: editForm.name,
        email: editForm.email || null,
        phone: editForm.phone || null,
        company: editForm.company || null,
        priority: editForm.priority,
        tags: editForm.tags_csv.split(',').map((t) => t.trim()).filter(Boolean),
      };
      await apiV1.put(`/leads/${id}`, payload);
      toast.success('Lead updated');
      setEditDialogOpen(false);
      fetchLead();
    } catch (error) {
      const message = error?.message || 'Failed to update lead';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-96 col-span-2" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="text-center py-12">
        <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="text-lg font-medium">Lead not found</h2>
        <p className="text-muted-foreground mb-4">The lead you're looking for doesn't exist.</p>
        <Button asChild>
          <Link to="/leads">Back to Leads</Link>
        </Button>
      </div>
    );
  }

  const stage = stageConfig[lead.stage] || { label: lead.stage, color: 'bg-slate-500' };
  const priority = priorityConfig[lead.priority] || { label: lead.priority, color: 'bg-slate-400' };
  const isClosed = lead.stage === 'won' || lead.stage === 'lost';

  return (
    <div className="space-y-6" data-testid="lead-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/leads">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{lead.name}</h1>
              <Badge className={`${stage.color} text-white`}>{stage.label}</Badge>
              <Badge className={`${priority.color} text-white`}>{priority.label} priority</Badge>
            </div>
            {lead.company && (
              <p className="text-muted-foreground text-sm mt-1">{lead.company}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setEditDialogOpen(true)} data-testid="lead-edit-button">
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>

          {!isClosed && (
            <Button onClick={handleConvert} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <CheckCircle className="h-4 w-4 mr-2" />
              Convert
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleStageChange('new')}>New</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStageChange('contacted')}>Contacted</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStageChange('qualified')}>Qualified</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStageChange('proposal')}>Proposal</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStageChange('negotiation')}>Negotiation</DropdownMenuItem>
              <DropdownMenuSeparator />
              {!isClosed && (
                <DropdownMenuItem onClick={handleMarkLost} className="text-destructive">
                  <XCircle className="h-4 w-4 mr-2" />
                  Mark Lost
                </DropdownMenuItem>
              )}
              <DropdownMenuItem className="text-destructive" onClick={handleDelete}>
                <Trash2 className="h-4 w-4 mr-2" />
                Archive
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main */}
        <div className="lg:col-span-2 space-y-6">
          <Tabs defaultValue="details" className="w-full">
            <TabsList>
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="activities">
                Activities {lead.activities?.length ? `(${lead.activities.length})` : ''}
              </TabsTrigger>
              <TabsTrigger value="tasks">
                Tasks {lead.tasks?.length ? `(${lead.tasks.length})` : ''}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4 mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-muted-foreground">Score</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Star className="h-4 w-4 text-amber-500" />
                        <span className="text-sm font-medium">{lead.score ?? 0}</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Source</Label>
                      <p className="text-sm font-medium mt-1 capitalize">
                        {lead.source_name || lead.source?.replace('_', ' ') || 'manual'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Created</Label>
                      <p className="text-sm font-medium mt-1">
                        {new Date(lead.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Last Contacted</Label>
                      <p className="text-sm font-medium mt-1">
                        {lead.last_contacted_at
                          ? new Date(lead.last_contacted_at).toLocaleDateString()
                          : 'Never'}
                      </p>
                    </div>
                    {lead.assigned_to_name && (
                      <div>
                        <Label className="text-sm text-muted-foreground">Assigned to</Label>
                        <p className="text-sm font-medium mt-1">{lead.assigned_to_name}</p>
                      </div>
                    )}
                    {lead.converted && (
                      <div>
                        <Label className="text-sm text-muted-foreground">Conversion value</Label>
                        <p className="text-sm font-medium mt-1">
                          {lead.conversion_value != null ? `$${lead.conversion_value}` : '—'}
                        </p>
                      </div>
                    )}
                  </div>

                  {lead.tags?.length > 0 && (
                    <div className="mt-4">
                      <Label className="text-sm text-muted-foreground">Tags</Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {lead.tags.map((tag, i) => (
                          <Badge key={i} variant="secondary">
                            <Tag className="w-3 h-3 mr-1" />
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {lead.tracking && Object.values(lead.tracking).some((v) => v) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Tracking</CardTitle>
                    <CardDescription>Attribution from the source</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      {lead.tracking.region_name && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-xs text-muted-foreground">Region</p>
                            <p className="text-sm font-medium">{lead.tracking.region_name}</p>
                          </div>
                        </div>
                      )}
                      {lead.tracking.office_name && (
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-xs text-muted-foreground">Office</p>
                            <p className="text-sm font-medium">{lead.tracking.office_name}</p>
                          </div>
                        </div>
                      )}
                      {lead.tracking.agent_name && (
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-xs text-muted-foreground">Agent</p>
                            <p className="text-sm font-medium">{lead.tracking.agent_name}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="activities" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Activity Log</CardTitle>
                  <CardDescription>Latest first</CardDescription>
                </CardHeader>
                <CardContent>
                  {(!lead.activities || lead.activities.length === 0) ? (
                    <p className="text-sm text-muted-foreground">No activities recorded yet.</p>
                  ) : (
                    <ScrollArea className="max-h-[400px] pr-3">
                      <ol className="relative border-l ml-3 space-y-4">
                        {[...lead.activities].reverse().map((a, idx) => (
                          <li key={a.id ?? idx} className="ml-4">
                            <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border border-white bg-primary" />
                            <div className="flex items-center gap-2">
                              <Activity className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                                {a.type}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {new Date(a.created_at).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-sm mt-1">{a.description}</p>
                            {a.metadata?.submission_id && (
                              <Link
                                to={`/submissions?submission=${a.metadata.submission_id}`}
                                className="text-xs text-primary hover:underline"
                              >
                                View submission
                              </Link>
                            )}
                          </li>
                        ))}
                      </ol>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="tasks" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Tasks</CardTitle>
                </CardHeader>
                <CardContent>
                  {(!lead.tasks || lead.tasks.length === 0) ? (
                    <p className="text-sm text-muted-foreground">No tasks yet.</p>
                  ) : (
                    <ul className="space-y-3">
                      {lead.tasks.map((t) => (
                        <li key={t.id} className="flex items-start gap-3">
                          <ClipboardList
                            className={`h-4 w-4 mt-1 ${t.completed ? 'text-emerald-500' : 'text-muted-foreground'}`}
                          />
                          <div className="flex-1">
                            <p className={`text-sm font-medium ${t.completed ? 'line-through text-muted-foreground' : ''}`}>
                              {t.title}
                            </p>
                            {t.description && (
                              <p className="text-xs text-muted-foreground mt-0.5">{t.description}</p>
                            )}
                            {t.due_date && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                Due {new Date(t.due_date).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          {t.completed && (
                            <Badge variant="secondary" className="text-xs">Done</Badge>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Contact</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {lead.email ? (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <a href={`mailto:${lead.email}`} className="text-primary hover:underline truncate">
                    {lead.email}
                  </a>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No email</p>
              )}
              {lead.phone ? (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <a href={`tel:${lead.phone}`} className="text-primary hover:underline">
                    {lead.phone}
                  </a>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No phone</p>
              )}
              {lead.company && (
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span>{lead.company}</span>
                </div>
              )}
              <Separator />
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                <span>Last activity {lead.last_activity_at ? new Date(lead.last_activity_at).toLocaleDateString() : '—'}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Edit Lead</DialogTitle>
            <DialogDescription>
              Update the lead's contact details and priority.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name *</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                data-testid="lead-edit-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Phone</Label>
                <Input
                  id="edit-phone"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-company">Company</Label>
              <Input
                id="edit-company"
                value={editForm.company}
                onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select
                value={editForm.priority}
                onValueChange={(v) => setEditForm({ ...editForm, priority: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-tags">Tags</Label>
              <Textarea
                id="edit-tags"
                rows={2}
                placeholder="comma, separated, tags"
                value={editForm.tags_csv}
                onChange={(e) => setEditForm({ ...editForm, tags_csv: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={saving || !editForm.name.trim()}
              data-testid="lead-edit-save-button"
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
